import type { ColumnProfile } from '../../api/datasets';
import { Distribution } from '../charts';
import { Badge } from '../ui';
import './ColumnProfileCard.css';

function formatNumber(value: number | null): string {
  if (value === null) return '—';
  return Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
}

function formatDate(value: string | null): string {
  return value ? value.slice(0, 10) : '—';
}

/** One column's profile: what it holds, what is missing, how it is spread. */
export function ColumnProfileCard({ column }: { column: ColumnProfile }) {
  const isNumeric = column.type === 'integer' || column.type === 'float';
  const isTemporal = column.type === 'date' || column.type === 'datetime';

  return (
    <article className="sc-column">
      <header className="sc-column__head">
        <div>
          <h3 className="sc-column__name">{column.source_name}</h3>
          <code className="sc-column__key">{column.name}</code>
        </div>
        <div className="sc-column__tags">
          <Badge srPrefix="Type">{column.type}</Badge>
          {column.is_unique && (
            <Badge tone="brand" srPrefix="Shape">
              🔑 Unique
            </Badge>
          )}
          {column.is_constant && (
            <Badge tone="warning" srPrefix="Shape">
              ⚠️ One value
            </Badge>
          )}
          {column.is_empty && (
            <Badge tone="danger" srPrefix="Shape">
              ⚠️ Empty
            </Badge>
          )}
        </div>
      </header>

      <dl className="sc-column__facts">
        <div>
          <dt>Missing</dt>
          <dd className="sc-numeric">
            {column.null_percentage}%{' '}
            <span className="sc-column__muted">({column.null_count.toLocaleString()})</span>
          </dd>
        </div>
        <div>
          <dt>Distinct</dt>
          <dd className="sc-numeric">{column.distinct_count.toLocaleString()}</dd>
        </div>

        {isNumeric && (
          <>
            <div>
              <dt>Range</dt>
              <dd className="sc-numeric">
                {formatNumber(column.minimum)} – {formatNumber(column.maximum)}
              </dd>
            </div>
            <div>
              <dt>Mean</dt>
              <dd className="sc-numeric">{formatNumber(column.mean)}</dd>
            </div>
            <div>
              <dt>Median</dt>
              <dd className="sc-numeric">{formatNumber(column.median)}</dd>
            </div>
            <div>
              <dt>Std. dev.</dt>
              <dd className="sc-numeric">{formatNumber(column.std_dev)}</dd>
            </div>
          </>
        )}

        {isTemporal && (
          <>
            <div>
              <dt>From</dt>
              <dd className="sc-numeric">{formatDate(column.earliest)}</dd>
            </div>
            <div>
              <dt>To</dt>
              <dd className="sc-numeric">{formatDate(column.latest)}</dd>
            </div>
            <div>
              <dt>Span</dt>
              <dd className="sc-numeric">
                {column.span_days === null ? '—' : `${column.span_days.toLocaleString()} days`}
              </dd>
            </div>
          </>
        )}

        {column.type === 'text' && (
          <div>
            <dt>Length</dt>
            <dd className="sc-numeric">
              {column.min_length ?? '—'}–{column.max_length ?? '—'}
            </dd>
          </div>
        )}
      </dl>

      {!column.is_empty && column.distribution.length > 0 && (
        <Distribution
          title={`Distribution of ${column.source_name}`}
          data={column.distribution.map((bin) => ({ label: bin.label, count: bin.count }))}
        />
      )}

      {!column.is_empty && column.distribution.length === 0 && (
        // An identifier has nothing to distribute; say so rather than draw a
        // row of identical bars.
        <p className="sc-column__note">
          Almost every value is different — this looks like an identifier.
        </p>
      )}
    </article>
  );
}
